import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { usePmsIntegrationEnabled } from '@/features/payments/hooks/usePmsIntegrationEnabled';
import { useAppSelector } from '@/store';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { UndergoingMaintenancePage } from '@/components/residents/maintenance/UndergoingMaintenancePage';

// Maintenance components
import { WorkRequestCard } from '@/components/residents/maintenance/mobile/WorkRequestCard';
import { MobileMaintenanceEmptyState } from '@/components/residents/maintenance/mobile/MobileMaintenanceEmptyState';
import { MobileCreateMaintenanceModal } from './MobileCreateMaintenanceModal';

type NavigationProp = StackNavigationProp<RootStackParamList>;

/**
 * Mobile Maintenance Screen
 * Matches new Figma design with dark theme, CTA card, and work request list
 */
export const MobileMaintenanceScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const pmsEnabled = usePmsIntegrationEnabled();
  const user = useAppSelector((s) => s.auth.user as any);
  
  // Maintenance data
  const { allTickets, loading, refresh } = useMaintenanceTickets();
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  // Handle new request press
  const handleNewRequestPress = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
    // Refresh the list after closing modal (in case a new request was created)
    refresh();
  }, [refresh]);

  // Handle card press - navigate to detail screen
  const handleCardPress = useCallback((ticket: IMaintenanceListResponse) => {
    navigation.navigate('MaintenanceDetail', { ticket });
  }, [navigation]);

  // Show maintenance page if PMS integration is disabled
  if (!pmsEnabled) {
    return <UndergoingMaintenancePage variant="disabled" />;
  }

  // Show future lease messaging if user's lease hasn't started yet
  if (user?.hasFutureLease) {
    return <UndergoingMaintenancePage variant="future" />;
  }

  const hasTickets = allTickets && allTickets.length > 0;

  // Render CTA card
  const renderCTACard = () => (
    <View style={styles.ctaCard}>
      <Text style={styles.ctaTitle}>Something broken?</Text>
      <TouchableOpacity
        style={styles.ctaButton}
        onPress={handleNewRequestPress}
        activeOpacity={0.8}
      >
        <Text style={styles.ctaButtonText}>New request</Text>
      </TouchableOpacity>
    </View>
  );

  // Render content
  const renderContent = () => {
    if (loading && !refreshing) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
        </View>
      );
    }

    if (!hasTickets) {
      return (
        <>
          {renderCTACard()}
          <MobileMaintenanceEmptyState />
        </>
      );
    }

    return (
      <>
        {renderCTACard()}
        <View style={styles.listContainer}>
          {allTickets?.map((ticket, index) => (
            <WorkRequestCard
              key={ticket.maintenanceRequestId || index}
              ticket={ticket}
              onPress={() => handleCardPress(ticket)}
            />
          ))}
        </View>
      </>
    );
  };

  return (
    <BaseResidentMobileScreen
      screenName="Maintenance"
      darkBackground={true}
    >
      <MobileScreenLayout
        backgroundColor={MARKETPLACE_COLORS.background}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshTintColor={MARKETPLACE_COLORS.textPrimary}
        lightStatusBar={true}
        contentGap={16}
      >
        {renderContent()}
      </MobileScreenLayout>

      {/* Create Maintenance Request Modal */}
      <MobileCreateMaintenanceModal
        visible={showCreateModal}
        onClose={handleModalClose}
      />
    </BaseResidentMobileScreen>
  );
};

const styles = StyleSheet.create({
  // CTA Card
  ctaCard: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ctaTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  ctaButton: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.black,
    lineHeight: 18,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },

  // List
  listContainer: {
    gap: 16,
  },
});

export default MobileMaintenanceScreen;
